import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, ViewChild } from '@angular/core';
import { DcFileUploadComponent } from '../../../ui-kit/dc-file-upload/dc-file-upload.component';
import { concat, defer, Observable, of, Subject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DcAlertService } from '../../../ui-kit/dc-alert/dc-alert.service';
import { catchError, first, map, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { FileDetails } from '../../../product-capture/import-product/import-product.consts';
import { HttpErrorResponse } from '@angular/common/http';
import { IdentifierPoolsService } from "../identifier-pools.service";
import { parse } from 'papaparse';
import { MODAL_CUSTOM_DATA } from "../../../utils/create-modal-injector";
import { IdentifierPool } from "../identifier-pools-model";

@Component({
  selector: 'app-identifier-pools-bulk-import-modal',
  templateUrl: './identifier-pools-bulk-import-modal.component.html',
  styleUrls: ['./identifier-pools-bulk-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IdentifierPoolsBulkImportModalComponent {
  protected readonly modal = inject(NgbActiveModal);
  private readonly identifierPoolsService = inject(IdentifierPoolsService);
  private readonly dcAlertService = inject(DcAlertService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(DcFileUploadComponent, {static: true})
  protected readonly fileUploader: DcFileUploadComponent;

  protected readonly processDone = signal(false);

  private readonly destroy$ = new Subject<boolean>();

  private readonly productsImported = signal([]);

  protected readonly modalData = inject(MODAL_CUSTOM_DATA) as { pool: IdentifierPool, parseOnly: boolean };

  importFiles(): void {
    this.processDone.set(false);
    this.productsImported.set([]);
    const errors = [];

    this.fileUploader
      .startQueue()
      .pipe(
        switchMap((results: FileDetails[]) => {
          const parsed$ = results.reduce(
            (
              acc: Observable<{ type: string; content: any }>[],
              current: FileDetails
            ) => {
              let parsedContent = null;
              try {
                parsedContent = parse(current.content, {
                  header: true,
                  skipEmptyLines: true,
                }).data;

                if (this.modalData.pool) {
                  parsedContent.forEach((row) => row.Pool = `${this.modalData.pool?.id}`);
                }

                acc.push(
                  of({type: current.file.type, content: parsedContent})
                );
              } catch (err) {
                current.error = `Error trying to read file: ${err}`;
              }
              return acc;
            },
            []
          );

          return concat(...parsed$).pipe(
            toArray(),
            switchMap((fileContents: { type: string; content: any }[]) => {
              const processes$ = fileContents.reduce(
                (
                  acc: Observable<any>[],
                  current: { type: string; content: any },
                  index: number
                ) => {
                  const detail = results[index];
                  detail.content = current.content;

                  acc.push(
                    defer(() => {
                      let req;

                      if (this.modalData.parseOnly) {
                        req = of(detail.content);
                      } else {
                        req = this.identifierPoolsService
                          .uploadIdentifierGroupsFile(detail.content)
                      }

                      detail.processing = true;

                      return req.pipe(
                        map((result: boolean) => {
                          detail.processing = false;
                          detail.processed = result;
                          detail.progress = detail.file.size;
                          this.productsImported.set([...this.productsImported(), detail]);

                          return result;
                        }),
                        catchError((err: HttpErrorResponse) => {
                          detail.error = err.error;
                          detail.processing = false;
                          detail.progress = detail.file.size;
                          errors.push(detail);

                          return of(err.error);
                        })
                      );
                    }).pipe(first())
                  );

                  return acc;
                },
                []
              );

              return concat(...processes$).pipe(toArray());
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
          this.processDone.set(true);
          this.changeDetectorRef.markForCheck();

          if (this.productsImported().length === this.fileUploader.files.length) {
            const resData = this.productsImported()[0].content
            setTimeout(() => this.close(this.modalData.parseOnly ? resData : null), 950);
          }
        },
        (error) => {
          this.changeDetectorRef.markForCheck();
          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Failed to import identifiers: ${error.error.errors[0]}`,
          });
          console.log(error);
        });
  }

  close(result?: any): void {
    this.fileUploader.destroy$.next(true);
    this.destroy$.next(true);
    this.destroy$.complete();
    this.modal.close(result ?? (this.productsImported()?.length ? 'success' : 'close'));
  }
}
